import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { customerGroupService } from "../services/customerGroupService";
import type { CustomerGroupInsert, CustomerGroupUpdate } from "../types";
import { toast } from "sonner";

export const useCustomerGroups = () => {
  const queryClient = useQueryClient();

  const query = useQuery({
    queryKey: ["customer-groups"],
    queryFn: () => customerGroupService.getAll(),
  });

  const activeQuery = useQuery({
    queryKey: ["customer-groups", "active"],
    queryFn: () => customerGroupService.getActive(),
  });

  const createMutation = useMutation({
    mutationFn: (data: CustomerGroupInsert) => customerGroupService.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["customer-groups"] });
      toast.success("Grupo criado com sucesso!");
    },
    onError: (error: Error) => {
      toast.error(`Erro ao criar grupo: ${error.message}`);
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: number; data: CustomerGroupUpdate }) =>
      customerGroupService.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["customer-groups"] });
      toast.success("Grupo atualizado com sucesso!");
    },
    onError: (error: Error) => {
      toast.error(`Erro ao atualizar grupo: ${error.message}`);
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: number) => customerGroupService.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["customer-groups"] });
      toast.success("Grupo excluído com sucesso!");
    },
    onError: (error: Error) => {
      toast.error(`Erro ao excluir grupo: ${error.message}`);
    },
  });

  return {
    groups: query.data,
    activeGroups: activeQuery.data,
    isLoading: query.isLoading || activeQuery.isLoading,
    error: query.error || activeQuery.error,
    createGroup: createMutation.mutate,
    updateGroup: updateMutation.mutate,
    deleteGroup: deleteMutation.mutate,
    isCreating: createMutation.isPending,
    isUpdating: updateMutation.isPending,
    isDeleting: deleteMutation.isPending,
  };
};
